/**
 * useHubV2State — All simulation-related state for Hub V2.
 *
 * Extracted from HubV2Simulator.tsx to allow sharing state between
 * the sidebar-stepper layout, step components, and results view.
 *
 * Dat bronbestand is op 8 september 2026 verwijderd: het werd nergens meer
 * gemonteerd. De route /crm/analyses/hub-v2 gaat via HubV2Layout, en deze hook
 * is sindsdien de enige plek waar die state leeft.
 *
 * MUST be used inside a SiteProfileProvider.
 */
import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useSiteProfile } from './useSiteProfile';
import { useHubProfileUpload } from './useHubV2ProfileUpload';
import { useFinancialModel } from './useFinancialModel';
import { FINANCIAL_DEFAULTS, type FinancialParams } from '../utils/financialDefaults';
import { runSimulation as runSim } from '../utils/simulationEngine';
import { deriveDemandProfile } from '../utils/piekToProfile';
import { usePiekAnalysesForOrg } from '@/hooks/usePiekAnalysesForOrg';
import { buildFleetDemandProfile, type FleetTypeConfig } from '../FleetProfileEditor';

export type ProfileMode = 'standaard' | 'upload';
export type DemandMode = 'standaard' | 'vloot' | 'upload' | 'piekanalyse';
export type BaseMode = 'piekanalyse' | 'upload' | 'handmatig';
export type EvMode = 'vloot' | 'handmatig';

type SimParamsSnapshot = {
  pv: number; bE: number; bP: number; eff: number; dod: number;
  buP: number; gMax: number; dem: number; deg: number; projYear: number;
};

const FLEET_WINDOWS: Record<string, { start: number; end: number; pct: number }[]> = {
  "Bus met laadbak":   [{ start: 16, end: 2, pct: 90 }, { start: 2, end: 16, pct: 10 }],
  "Combi Afzet":       [{ start: 16, end: 2, pct: 100 }],
  "Combi KV-D/E LZV":  [{ start: 16, end: 2, pct: 100 }],
  "Trekker":           [{ start: 16, end: 2, pct: 90 }, { start: 2, end: 16, pct: 10 }],
  "Trekker 6x2 VLA":   [{ start: 16, end: 2, pct: 70 }, { start: 2, end: 16, pct: 30 }],
  "Trekker Bulk":      [{ start: 16, end: 2, pct: 50 }, { start: 2, end: 16, pct: 50 }],
  "Trekker DC":        [{ start: 16, end: 2, pct: 95 }, { start: 2, end: 16, pct: 5 }],
  "Trekker LWD":       [{ start: 16, end: 2, pct: 95 }, { start: 2, end: 16, pct: 5 }],
};

export function useHubV2State(initialParams?: Record<string, any>, orgId?: string) {
  const { profile, profiles, setProfileById } = useSiteProfile();

  // Profile data
  const SP = profile.solarProfile;
  const FLEET = profile.fleet;
  const CHP = profile.chargingProfile;
  const CSF = profile.seasonalFactors;
  const DOCS = profile.docs;
  const solarRef = profile.solarRefCapacity;

  // Session persistence
  const simStorageKey = `sim_state_${profile.id}`;
  const loadSimState = () => {
    try {
      const raw = sessionStorage.getItem(simStorageKey);
      if (raw) return JSON.parse(raw);
    } catch {}
    return null;
  };
  const savedSim = loadSimState();

  // ─── PARAMETER STATE ───
  const [pv, setPv] = useState(initialParams?.pv ?? savedSim?.pv ?? profile.defaults.pv);
  const [bE, setBE] = useState(initialParams?.bE ?? savedSim?.bE ?? profile.defaults.bE);
  const [bP, setBP] = useState(initialParams?.bP ?? savedSim?.bP ?? profile.defaults.bP);
  const [eff, setEff] = useState(initialParams?.eff ?? savedSim?.eff ?? profile.defaults.eff);
  const [dod, setDod] = useState(initialParams?.dod ?? savedSim?.dod ?? profile.defaults.dod);
  const [buP, setBuP] = useState(initialParams?.buP ?? savedSim?.buP ?? profile.defaults.buP);
  const [gMax, setGMax] = useState(() => { const v = initialParams?.gMax ?? savedSim?.gMax ?? profile.defaults.gMax; return v > 50 ? v / 1000 : v; });
  const [dem, setDem] = useState(initialParams?.dem ?? savedSim?.dem ?? profile.defaults.dem);
  const [deg, setDeg] = useState(initialParams?.deg ?? savedSim?.deg ?? profile.defaults.deg);
  const [projYear, setProjYear] = useState(initialParams?.projYear ?? savedSim?.projYear ?? 1);

  // ─── BLOKSTROOM ───
  const [blokstroomEnabled, setBlokstroomEnabled] = useState(false);
  const [blokstroomPower, setBlokstroomPower] = useState(2);
  const [blokstroomStart, setBlokstroomStart] = useState(0);
  const [blokstroomEnd, setBlokstroomEnd] = useState(6);

  // Reset on profile change
  const prevProfileRef = useRef(profile.id);
  useEffect(() => {
    if (prevProfileRef.current !== profile.id) {
      prevProfileRef.current = profile.id;
      sessionStorage.removeItem(simStorageKey);
      setPv(profile.defaults.pv); setBE(profile.defaults.bE); setBP(profile.defaults.bP);
      setEff(profile.defaults.eff); setDod(profile.defaults.dod); setBuP(profile.defaults.buP);
      setGMax(profile.defaults.gMax); setDem(profile.defaults.dem); setDeg(profile.defaults.deg);
      setProjYear(1);
    }
  }, [profile.id]);

  // ─── FINANCIAL ───
  const [financialParams, setFinancialParams] = useState<FinancialParams>(() => ({ ...FINANCIAL_DEFAULTS }));

  // ─── MODES ───
  const [solarMode, setSolarMode] = useState<ProfileMode>(initialParams?.solarMode ?? 'standaard');
  const [gridMode, setGridMode] = useState<ProfileMode>(initialParams?.gridMode ?? 'standaard');
  // Legacy demandMode kept for backwards compat with old saved simulations
  const [demandMode, setDemandMode] = useState<DemandMode>(initialParams?.demandMode ?? 'standaard');
  const [selectedPiekId, setSelectedPiekId] = useState<string | null>(initialParams?.selectedPiekId ?? null);

  // ─── TWO-LAYER DEMAND MODEL ───
  // Migration: old saves have `dem` but not `baseDem`. Use `dem` as fallback for baseDem.
  const legacyDem = initialParams?.dem ?? savedSim?.dem ?? profile.defaults.dem;
  // Base load: what the site currently draws from the grid
  const [baseMode, setBaseMode] = useState<BaseMode>(initialParams?.baseMode ?? 'handmatig');
  const [baseDem, setBaseDem] = useState(initialParams?.baseDem ?? savedSim?.baseDem ?? legacyDem);
  const [baseIncludesEv, setBaseIncludesEv] = useState(initialParams?.baseIncludesEv ?? false);
  // EV charging: additional load on top of base
  // Default evEnabled=false for old saves (they have combined dem, EV was baked in)
  const hasNewDemandModel = initialParams?.baseDem != null || savedSim?.baseDem != null;
  const [evEnabled, setEvEnabled] = useState(initialParams?.evEnabled ?? (hasNewDemandModel ? true : false));
  const [evMode, setEvMode] = useState<EvMode>(initialParams?.evMode ?? 'vloot');
  const [evDem, setEvDem] = useState(initialParams?.evDem ?? savedSim?.evDem ?? 0);

  // ─── PIEKANALYSE ───
  const { data: piekAnalyses } = usePiekAnalysesForOrg(orgId);
  const piekProfile = useMemo(() => {
    if (!selectedPiekId || !piekAnalyses) return null;
    const analysis = piekAnalyses.find((a: any) => a.id === selectedPiekId);
    if (!analysis?.raw_data) return null;
    return deriveDemandProfile(analysis.raw_data as any);
  }, [selectedPiekId, piekAnalyses]);

  // ─── FLEET ───
  const [fleetConfig, setFleetConfig] = useState<FleetTypeConfig[]>(() =>
    FLEET.map(f => ({
      type: f.type,
      n: f.n,
      kwhPerVehicle: Math.round(f.mwh * 1e3 / f.n),
      windows: FLEET_WINDOWS[f.type] ?? [
        { start: 16, end: 2, pct: 72 },
        { start: 2, end: 16, pct: 28 },
      ],
    }))
  );

  const [fleetHourly, fleetTotalKwh] = useMemo(() => buildFleetDemandProfile(fleetConfig), [fleetConfig]);
  const fleetDemMwh = fleetTotalKwh / 1e3;

  const fleetCHP = useMemo(() => {
    const total = fleetHourly.reduce((a, b) => a + b, 0);
    if (total <= 0) return CHP;
    const avg = total / 24;
    return fleetHourly.map(h => h / avg);
  }, [fleetHourly, CHP]);

  // ─── UPLOADS ───
  const {
    profiles: uploadedProfiles, errors: uploadErrors, loading: uploadLoading,
    handleSolarUpload, handleConsumptionUpload, handleGridUpload,
    clearSolar, clearConsumption, clearGrid, restoreProfiles,
  } = useHubProfileUpload();

  const profilesRestoredRef = useRef(false);
  useEffect(() => {
    if (!profilesRestoredRef.current && initialParams?.uploadedProfiles) {
      profilesRestoredRef.current = true;
      restoreProfiles(initialParams.uploadedProfiles);
    }
  }, [initialParams, restoreProfiles]);

  // ─── DE GEUPLOADE NETPIEK WORDT DE NETAANSLUITING ───
  //
  // De knop belooft dit al sinds hij bestaat: "Verbruiksprofiel importeren
  // (Piekvermogen)". Tot 8 september 2026 deed hij het niet. Het bestand werd
  // gelezen, geparseerd, opgeslagen, bewaard en getoond ("✅ Grid profiel
  // geladen", met datapunten en jaarverbruik) — en door geen enkele plek in de
  // simulatie gebruikt. Wie in upload-modus stond zag bovendien de schuif "Grid
  // Max" verdwijnen terwijl de badge ernaast onveranderd `{gMax} MW` bleef
  // tonen: de oude handmatige waarde, met de suggestie dat die uit het bestand
  // kwam.
  //
  // Waarom gMax zetten en niet een aparte afgeleide waarde: gMax loopt door de
  // simulatie, de snapshots, de scenario's en de opslag. Eén parallelle waarde
  // ernaast zou al die plekken moeten kennen, en de eerste die hem vergeet
  // maakt precies het gat dat we hier dichten.
  //
  // De piek is de hoogste losse kwartierwaarde, niet een uurgemiddelde: een
  // aansluiting moet de piek aankunnen. Terug naar 'standaard' geeft de schuif
  // weer terug, met deze waarde als vertrekpunt.
  const gridPeakKw = uploadedProfiles.gridPeakKw;
  useEffect(() => {
    if (gridMode !== 'upload' || !gridPeakKw || gridPeakKw <= 0) return;
    const afgeleidMw = Math.round((gridPeakKw / 1000) * 100) / 100;
    setGMax(prev => (prev === afgeleidMw ? prev : afgeleidMw));
  }, [gridMode, gridPeakKw]);

  // ─── RESOLVED PROFILES ───
  const activeSP = useMemo(() => {
    if (solarMode === 'upload' && uploadedProfiles.solarProfile) return uploadedProfiles.solarProfile;
    return SP;
  }, [solarMode, uploadedProfiles.solarProfile]);

  // Layer 0: Base load profile (grid consumption)
  const resolvedBaseDem = useMemo(() => {
    if (baseMode === 'piekanalyse' && piekProfile) return piekProfile.annualMwh;
    if (baseMode === 'upload' && uploadedProfiles.consumptionDemandMwh) return uploadedProfiles.consumptionDemandMwh;
    return baseDem;
  }, [baseMode, piekProfile, uploadedProfiles.consumptionDemandMwh, baseDem]);

  const chpBase = useMemo(() => {
    if (baseMode === 'piekanalyse' && piekProfile) return piekProfile.chp;
    if (baseMode === 'upload' && uploadedProfiles.consumptionProfile) return uploadedProfiles.consumptionProfile;
    return CHP; // flat or default profile
  }, [baseMode, piekProfile, uploadedProfiles.consumptionProfile, CHP]);

  const csfBase = useMemo(() => {
    if (baseMode === 'piekanalyse' && piekProfile) return piekProfile.csf;
    if (baseMode === 'upload' && uploadedProfiles.consumptionSeasonalFactors)
      return uploadedProfiles.consumptionSeasonalFactors;
    return CSF;
  }, [baseMode, piekProfile, uploadedProfiles.consumptionSeasonalFactors, CSF]);

  // Layer 1: EV charging profile
  const resolvedEvDem = useMemo(() => {
    if (!evEnabled) return 0;
    if (evMode === 'vloot') return fleetDemMwh;
    return evDem;
  }, [evEnabled, evMode, fleetDemMwh, evDem]);

  const chpEv = useMemo(() => {
    if (!evEnabled || resolvedEvDem <= 0) return Array(24).fill(1) as number[];
    if (evMode === 'vloot') return fleetCHP;
    return CHP; // default charging shape
  }, [evEnabled, resolvedEvDem, evMode, fleetCHP, CHP]);

  const csfEv = useMemo(() => {
    // EV charging usually has flat seasonality (trucks charge year-round)
    return Array(12).fill(1) as number[];
  }, []);

  // Combined (for display and backwards compat)
  const resolvedDem = resolvedBaseDem + resolvedEvDem;

  // Legacy compat: activeCHP/activeCSF for components that still use them
  const activeCHP = chpBase;
  const activeCSF = csfBase;

  const effectiveBE = useMemo(() => bE * Math.pow(1 - deg / 100, projYear - 1), [bE, deg, projYear]);
  const degPct = bE > 0 ? (effectiveBE / bE) * 100 : 100;

  // ─── SESSION STORAGE PERSIST ───
  const getParams = useCallback(() => {
    const params: Record<string, any> = {
      pv, bE, bP, eff, dod, buP, gMax, dem, deg, projYear,
      blokstroomEnabled, blokstroomPower, blokstroomStart, blokstroomEnd,
      solarMode, gridMode, demandMode,
    };
    if (uploadedProfiles.consumptionProfile || uploadedProfiles.solarProfile || uploadedProfiles.gridProfile) {
      params.uploadedProfiles = {
        consumptionProfile: uploadedProfiles.consumptionProfile,
        consumptionDemandMwh: uploadedProfiles.consumptionDemandMwh,
        consumptionStats: uploadedProfiles.consumptionStats,
        consumptionSeasonalFactors: uploadedProfiles.consumptionSeasonalFactors || null,
        solarProfile: uploadedProfiles.solarProfile,
        solarStats: uploadedProfiles.solarStats,
        gridProfile: uploadedProfiles.gridProfile,
        gridPeakKw: uploadedProfiles.gridPeakKw,
        gridDemandMwh: uploadedProfiles.gridDemandMwh,
        gridStats: uploadedProfiles.gridStats,
      };
    }
    params.activeCSF = activeCSF;
    params.activeCHP = activeCHP;
    params.resolvedDem = resolvedDem;
    if (selectedPiekId) params.selectedPiekId = selectedPiekId;
    if (piekProfile) {
      params.piekCHP = piekProfile.chp;
      params.piekCSF = piekProfile.csf;
      params.piekDemMwh = piekProfile.annualMwh;
    }
    return params;
  }, [pv, bE, bP, eff, dod, buP, gMax, dem, deg, projYear, blokstroomEnabled, blokstroomPower, blokstroomStart, blokstroomEnd, solarMode, gridMode, demandMode, uploadedProfiles, activeCSF, activeCHP, resolvedDem, selectedPiekId, piekProfile]);

  useEffect(() => {
    sessionStorage.setItem(simStorageKey, JSON.stringify(getParams()));
  }, [getParams, simStorageKey]);

  // ─── SCENARIOS ───
  const scenarios = profile.scenarios.map(s => ({ ...s }));
  function applySc(v: any) {
    setPv(v.pv); setBE(v.bE); setBP(v.bP); setEff(v.eff); setDod(v.dod);
    setBuP(v.buP); setGMax(v.gMax); setDem(v.dem); setDeg(v.deg); setProjYear(v.projYear);
    setDemandMode('standaard'); setSolarMode('standaard'); setGridMode('standaard');
  }

  // ─── UNDO/REDO ───
  const getSnapshot = (): SimParamsSnapshot => ({ pv, bE, bP, eff, dod, buP, gMax, dem, deg, projYear });
  const applySnapshot = (s: SimParamsSnapshot) => {
    setPv(s.pv); setBE(s.bE); setBP(s.bP); setEff(s.eff); setDod(s.dod);
    setBuP(s.buP); setGMax(s.gMax); setDem(s.dem); setDeg(s.deg); setProjYear(s.projYear);
  };

  const [simPast, setSimPast] = useState<SimParamsSnapshot[]>([]);
  const [simFuture, setSimFuture] = useState<SimParamsSnapshot[]>([]);
  const lastSnapshotRef = useRef<string>(JSON.stringify(getSnapshot()));

  useEffect(() => {
    const current = JSON.stringify(getSnapshot());
    if (current !== lastSnapshotRef.current) {
      const prev: SimParamsSnapshot = JSON.parse(lastSnapshotRef.current);
      setSimPast(p => [...p.slice(-49), prev]);
      setSimFuture([]);
      lastSnapshotRef.current = current;
    }
  }, [pv, bE, bP, eff, dod, buP, gMax, dem, deg, projYear]);

  const simUndo = useCallback(() => {
    if (simPast.length === 0) return;
    const snap = getSnapshot();
    setSimFuture(f => [snap, ...f.slice(0, 49)]);
    const p = [...simPast];
    const last = p.pop()!;
    setSimPast(p);
    applySnapshot(last);
    lastSnapshotRef.current = JSON.stringify(last);
  }, [simPast, pv, bE, bP, eff, dod, buP, gMax, dem, deg, projYear]);

  const simRedo = useCallback(() => {
    if (simFuture.length === 0) return;
    const snap = getSnapshot();
    setSimPast(p => [...p.slice(-49), snap]);
    const f = [...simFuture];
    const next = f.shift()!;
    setSimFuture(f);
    applySnapshot(next);
    lastSnapshotRef.current = JSON.stringify(next);
  }, [simFuture, pv, bE, bP, eff, dod, buP, gMax, dem, deg, projYear]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
        e.preventDefault();
        if (e.shiftKey) simRedo(); else simUndo();
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'y') {
        e.preventDefault();
        simRedo();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [simUndo, simRedo]);

  // ─── SIMULATION ENGINE ───
  const r = useMemo(() => {
    return runSim({
      SP: activeSP, solarRef, pv, bE, bP, eff, dod,
      buP, gMax, deg, projYear,
      // Two-layer demand
      demBase: resolvedBaseDem, chpBase, csfBase,
      demEv: resolvedEvDem, chpEv, csfEv,
      blok: { enabled: blokstroomEnabled, power: blokstroomPower, start: blokstroomStart, end: blokstroomEnd },
    });
  }, [pv, bE, bP, eff, dod, buP, gMax, resolvedBaseDem, resolvedEvDem, chpBase, csfBase, chpEv, csfEv, activeSP, solarRef, deg, projYear, blokstroomEnabled, blokstroomPower, blokstroomStart, blokstroomEnd]);

  // ─── FINANCIAL MODEL ───
  const numTrucks = demandMode === 'vloot'
    ? fleetConfig.reduce((s, f) => s + f.n, 0)
    : FLEET.reduce((s, f) => s + f.n, 0);

  // `totalDelivered` is de VRAAG (`r.dem`); `unmet` is het deel daarvan dat net,
  // batterij en aggregaat niet konden dekken. Zonder dat veld geeft
  // `deliveredEnergy()` NaN en rekent `useFinancialModel` hieronder NPV, IRR,
  // LCOE en marge als NaN — gemeten met exact deze objectvorm. `financialSim`
  // voedt bovendien de "Geleverd"-kaart en de ongedekt-banner in
  // steps/StepResultaten.tsx, zodat dat scherm hetzelfde getal toont als de
  // omzetbasis van de businesscase: op de Wognum-invoer (pv 2 MWp, 20 MWh/10 MW
  // BESS, 0,33 MW aansluiting, geen aggregaat, 19.000 MWh, jaar 15) 4.149,9 in
  // plaats van 18.994,3 MWh — 14.844,4 MWh (78,2 %) ongedekt.
  const financialSim = useMemo(() => ({
    pvProduction: r.pv, totalDelivered: r.dem, gridImport: r.gi,
    gridExport: r.ge, backupUsed: r.bu, curtailment: r.curt, bLoss: r.bLoss,
    blokstroomImport: r.blokstroomImport ?? 0,
    unmet: r.unmet,
  }), [r]);

  const financialSys = useMemo(() => ({
    pvCapacity: pv, bessEnergy: bE, bessPower: bP, backupPower: buP, gridMax: gMax, numTrucks,
  }), [pv, bE, bP, buP, gMax, numTrucks]);

  const financialModel = useFinancialModel(financialSim, financialSys, financialParams, deg);

  const handleFinancialParamChange = useCallback((key: keyof FinancialParams, value: number) => {
    setFinancialParams(prev => ({ ...prev, [key]: value }));
  }, []);

  return {
    // Profile
    profile, profiles, setProfileById,
    SP, FLEET, CHP, CSF, DOCS, solarRef,
    // Energy params + setters
    pv, setPv, bE, setBE, bP, setBP, eff, setEff, dod, setDod,
    buP, setBuP, gMax, setGMax, dem, setDem, deg, setDeg,
    projYear, setProjYear,
    // Blokstroom
    blokstroomEnabled, setBlokstroomEnabled,
    blokstroomPower, setBlokstroomPower,
    blokstroomStart, setBlokstroomStart,
    blokstroomEnd, setBlokstroomEnd,
    // Modes
    solarMode, setSolarMode, gridMode, setGridMode,
    demandMode, setDemandMode, // legacy compat
    selectedPiekId, setSelectedPiekId,
    // Two-layer demand
    baseMode, setBaseMode, baseDem, setBaseDem,
    baseIncludesEv, setBaseIncludesEv,
    evEnabled, setEvEnabled, evMode, setEvMode, evDem, setEvDem,
    resolvedBaseDem, resolvedEvDem,
    chpBase, csfBase, chpEv, csfEv,
    // Piekanalyse
    piekAnalyses, piekProfile,
    // Fleet
    fleetConfig, setFleetConfig, fleetCHP, fleetDemMwh, fleetHourly,
    // Upload
    uploadedProfiles, uploadErrors, uploadLoading,
    handleSolarUpload, handleConsumptionUpload, handleGridUpload,
    clearSolar, clearConsumption, clearGrid,
    // Resolved profiles (backwards compat + combined)
    activeSP, activeCHP, activeCSF, resolvedDem,
    effectiveBE, degPct,
    // Simulation result
    r,
    // Financial
    financialParams, setFinancialParams, financialModel,
    financialSim, financialSys, numTrucks, handleFinancialParamChange,
    // Undo/redo
    simUndo, simRedo, simPast, simFuture,
    // Scenarios
    scenarios, applySc,
    // Params getter (for saving)
    getParams,
  };
}

export type HubV2State = ReturnType<typeof useHubV2State>;
